import * as React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { prop, theme } from 'styled-tools'
import LoadingIndicator from '../../loadingIndicator'
import features from '../../features'
import { LightboxOverlay, OverlayCloseButton } from '../../overlay'
import settings from '../../settings'
import { Container, Row, Column } from '../../grid'
import Button from '../../button/baseButton'
import Headline from '../../typography/headline'
import Paragraph from '../../typography/paragraph'
import InputField from '../../customer-form/InputField'
import {
  retrieveAddress,
  searchAddresses,
} from '../../../../utils/badaAppointmentApi'

const InputGroup = styled(Column)`
  button {
    width: 100%;
    padding-left: 5px;
    padding-right: 5px;
  }
  input {
    font-size: 16px;
  }
  ${(props) => props.theme.media.min.sm`
    display: flex;
    align-items: flex-start;

    > span {
      margin-right: 10px;
      margin-bottom: 0;
      flex: 1 1 60%;
    }
    button {
      flex: 1 1 40%;
      margin:0;
      font-size: ${prop('theme.button.fontSize', '12px')};
    }
    `}
`

const OverlayContent = styled(Container)`
  position: relative;
  padding: 25px 40px 40px;
  background-color: ${theme('postalCodeOverlay.backgroundColor')};
  max-width: 600px;
  margin: 0;
  min-height: 250px;
  border-radius: ${theme('postalCodeOverlay.borderRadius')};
`

const StyledColumn = styled(Column)`
  padding-top: ${theme('postalCodeOverlay.text.paddingTop')};
  padding-bottom: ${theme('postalCodeOverlay.text.paddingBottom')};
`

const StyledButton = styled(Button)``

const StyledInputField = styled(InputField)`
  height: ${theme('postalCodeOverlay.inputField.height')};
  border-radius: ${theme('postalCodeOverlay.inputField.borderRadius')};
  padding: ${theme('postalCodeOverlay.inputField.padding')};

  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }
`

const StyledCloseButton = styled(OverlayCloseButton)`
  position: absolute;
  top: 20px;
  color: #333;
  right: 20px;
  margin-bottom: 0;
  margin-right: 0;
`

const StyledParagraph = styled(Paragraph)`
  font-weight: ${theme('postalCodeOverlay.text.fontWeight')};
  margin-bottom: 0;
`

const StyledHeadline = styled(Headline)`
  margin: 0;
`

const regex = new RegExp(settings.postcodeRegex)

const postcodeFormatter = (code) =>
  `${code || ''}`.replace(regex, (m, ...rest) => {
    const groups = rest.slice(0, -2).filter(Boolean)
    if (!groups.length) {
      return m
    }
    return groups.join(' ')
  })

class PostalCode extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      input: '',
      searchFailed: false,
      loading: false,
      valid: undefined,
    }
    this.postcodeInputRef = React.createRef()
    this.onCancel = this.onCancel.bind(this)
    this.findCity = this.findCity.bind(this)
  }

  searchAddress(requirePostCode, refinementId) {
    const postcode = postcodeFormatter(this.state.input)
    return searchAddresses({
      searchText: postcode,
      countries: settings.addressCountry || this.props.brandCountry,
      limit: 10,
      refinementId,
      filterPostcode: postcode,
    }).then((addresses) => {
      if (addresses.length < 1) {
        return undefined
      }
      const address = addresses.filter(({ type }) => type === 'Address')
      const postcodes = addresses.filter(({ type }) => type === 'Postcode')
      if (requirePostCode && postcodes.length) {
        return this.searchAddress(false, postcodes[0].id)
      }
      return address.length > 0
        ? address[0]
        : this.searchAddress(false, addresses[0].id)
    })
  }

  findCity() {
    if (features.requirePostCode) {
      this.setState({ valid: undefined, loading: false })
      this.props.onChange({
        line1: this.state.input,
        postalCode: this.state.input,
        countryName: this.props.brandCountry,
        city: '',
      })

      return
    }

    this.setState({ loading: true })

    this.searchAddress(true)
      .then((address) => {
        if (!address) {
          return undefined
        }

        return retrieveAddress(address.id)
      })
      .then((address) => {
        this.setState({ valid: address ? undefined : false, loading: false })
        if (address) {
          this.props.onChange(address)
        }
      })
  }

  componentDidMount() {
    this.postcodeInputRef.current.focus()
  }

  onCancel() {
    this.setState({
      input: '',
      valid: undefined,
    })
    this.props.setClosed()
  }

  render() {
    return (
      <LightboxOverlay setClosed={this.onCancel}>
        <OverlayContent>
          <Row justifyContent="center">
            <Column>
              <StyledHeadline>{this.props.headline}</StyledHeadline>
            </Column>
            <StyledColumn>
              <StyledParagraph size="large">{this.props.label}</StyledParagraph>
            </StyledColumn>
            {this.state.loading ? (
              <StyledColumn>
                <LoadingIndicator />
              </StyledColumn>
            ) : (
              <InputGroup>
                <StyledInputField
                  ref={this.inputRef}
                  inputRef={this.postcodeInputRef}
                  placeholder={this.props.placeholder}
                  autocomplete="postal-code"
                  id="postalCode"
                  value={this.state.input}
                  valid={this.state.valid}
                  error={this.props.error}
                  disabled={this.state.loading}
                  type={features.requirePostCode ? 'string' : 'number'}
                  onChange={(e) =>
                    this.setState({ input: e.target.value, valid: undefined })
                  }
                  onKeyUp={(e) => {
                    if (e.keyCode === 13) this.findCity()
                  }}
                />
                <StyledButton
                  onClick={this.findCity}
                  disabled={this.state.loading}
                >
                  {this.props.buttonText}
                </StyledButton>
              </InputGroup>
            )}
          </Row>
          <StyledCloseButton
            inverted
            onClick={this.onCancel}
            title={this.props.cancelText}
          />
        </OverlayContent>
      </LightboxOverlay>
    )
  }
}

PostalCode.propTypes = {
  client: PropTypes.shape({
    query: PropTypes.func.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  setClosed: PropTypes.func,
  label: PropTypes.string,
  error: PropTypes.string,
  buttonText: PropTypes.string,
  cancelText: PropTypes.string,
  placeholder: PropTypes.string,
  headline: PropTypes.string,
}

PostalCode.defaultProps = {
  label: null,
  error: null,
  buttonText: null,
  cancelText: null,
  headline: null,
  placeholder: null,
  setClosed: () => {},
}

export default PostalCode
